import * as React from "react";
import { cn } from "@/lib/utils";

export interface MenuButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Whether the menu is open (X shape) or closed (hamburger)
   */
  isOpen?: boolean;
  /**
   * Callback when the button is clicked
   */
  onToggle?: () => void;
  /**
   * Enable line-by-line drawing animation (for entrance animation)
   * When true, lines will draw sequentially based on animateStep
   * @default false
   */
  animateIn?: boolean;
  /**
   * Current animation step (0-3) when animateIn is true
   * 0 = all lines hidden, 1 = first line, 2 = second line, 3 = all lines visible
   * @default 0
   */
  animateStep?: 0 | 1 | 2 | 3;
}

/**
 * MenuButton - Animated hamburger menu button
 *
 * An interactive hamburger menu button that animates between:
 * - Closed state: Three horizontal bars (hamburger icon)
 * - Open state: X shape (close icon)
 *
 * Features:
 * - Smooth CSS transitions with sequential bar animations
 * - High-fidelity reproduction of Figma design
 * - Accessible button with proper ARIA attributes
 * - Customizable via className prop
 *
 * AnimateIn Mode:
 * - Top bar: draws right → left (anchored on right edge)
 * - Middle bar: draws right → left (anchored on right edge)
 * - Bottom bar: draws left → right (anchored on left edge)
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * <MenuButton isOpen={isOpen} onToggle={() => setIsOpen(!isOpen)} />
 * ```
 */
export const MenuButton = React.forwardRef<HTMLButtonElement, MenuButtonProps>(
  ({ className, isOpen = false, onToggle, animateIn = false, animateStep = 0, ...props }, ref) => {
    // Calculate line widths for animateIn mode
    // When animateIn is true, lines draw sequentially based on animateStep
    const getLineWidth = (lineIndex: 1 | 2 | 3): string => {
      if (!animateIn) {
        // Normal mode - all lines visible (unless menu is open and middle line hides)
        return isOpen && lineIndex === 2 ? "0" : "1.875rem";
      }
      // Animate-in mode - lines appear sequentially
      return animateStep >= lineIndex ? "1.875rem" : "0";
    };

    // Button is 2.25rem (36px), bar is 1.875rem (30px)
    // Side margin for centering: (2.25 - 1.875) / 2 = 0.1875rem (3px)
    const sideMargin = "0.1875rem";

    // Get horizontal positioning based on animation direction
    // Top/middle bars: right → left (anchored on right)
    // Bottom bar: left → right (anchored on left)
    const getHorizontalPosition = (lineIndex: 1 | 2 | 3): React.CSSProperties => {
      if (!animateIn) {
        // Normal mode - center the bars
        return { left: sideMargin, right: sideMargin };
      }
      // AnimateIn mode - anchor on one side for directional drawing
      if (lineIndex === 3) {
        // Bottom bar: anchor left, expands rightward
        return { left: sideMargin, right: "auto" };
      }
      // Top and middle bars: anchor right, expand leftward
      return { right: sideMargin, left: "auto" };
    };

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          "relative flex h-[2.25rem] w-[2.25rem] flex-col items-center justify-center gap-[0.375rem]",
          "transition-all duration-300 ease-in-out",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900",
          className
        )}
        onClick={onToggle}
        aria-label={isOpen ? "Close menu" : "Open menu"}
        aria-expanded={isOpen}
        {...props}
      >
        {/* Top bar - draws right → left in animateIn mode */}
        <span
          className={cn(
            "absolute h-[4px] bg-white transition-all duration-300 ease-in-out",
            isOpen
              ? "rotate-45"
              : "-translate-y-[0.625rem] rotate-0"
          )}
          style={{
            width: getLineWidth(1),
            ...getHorizontalPosition(1),
            transitionDelay: isOpen ? "100ms" : animateIn ? "0ms" : "0ms",
          }}
        />

        {/* Middle bar - draws right → left in animateIn mode */}
        <span
          className={cn(
            "absolute h-[4px] bg-white transition-all duration-300 ease-in-out",
            isOpen ? "opacity-0" : "opacity-100"
          )}
          style={{
            width: getLineWidth(2),
            ...getHorizontalPosition(2),
            transitionDelay: isOpen ? "0ms" : animateIn ? "0ms" : "100ms",
          }}
        />

        {/* Bottom bar - draws left → right in animateIn mode */}
        <span
          className={cn(
            "absolute h-[4px] bg-white transition-all duration-300 ease-in-out",
            isOpen
              ? "-rotate-45"
              : "translate-y-[0.625rem] rotate-0"
          )}
          style={{
            width: getLineWidth(3),
            ...getHorizontalPosition(3),
            transitionDelay: isOpen ? "100ms" : animateIn ? "0ms" : "0ms",
          }}
        />
      </button>
    );
  }
);

MenuButton.displayName = "MenuButton";
